import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import Footer from './Footer';
import { getImageUrl, getApiUrl } from '../utils/imageHelper';
import { useCity } from '../contexts/CityContext';

const GAME_TYPE_OPTIONS = [
  'All Types', 'Strategy', 'Party Games', 'RPG', 'Card Games', 'Cooperative',
];

const SORT_OPTIONS = [
  { label: 'Most Members', value: 'members' },
  { label: 'Newest', value: 'newest' },
  { label: 'A–Z', value: 'az' },
];

function ClubCard({ club, onClick }) {
  const gameList = (club.games || '').split(',').filter(Boolean).slice(0, 3);
  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 16px 40px rgba(0,0,0,0.12)' }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className="bg-white rounded-2xl shadow-sm overflow-hidden cursor-pointer flex flex-col group"
    >
      <div className="h-44 overflow-hidden relative">
        <img
          src={getImageUrl(club.image)}
          alt={club.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={e => { e.target.src = 'https://placehold.co/400x200/6366f1/white?text=Club'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <span className="text-white text-xs font-semibold bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
            </svg>
            {club.memberCount || club.members || 0} members
          </span>
        </div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-bold text-gray-900 mb-1 line-clamp-1 text-base">{club.name}</h3>
        <p className="text-xs text-gray-500 flex items-center gap-1 mb-3">
          <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          {club.location || 'Location TBD'}
        </p>
        {club.meetingDay && club.meetingDay !== 'Contact for details' && (
          <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Meets: {club.meetingDay}
          </p>
        )}
        {gameList.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-auto">
            {gameList.map(g => (
              <span key={g} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                {g.trim()}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function FindClubs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { city } = useCity();

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('location') || '');
  const [selectedGameType, setSelectedGameType] = useState(searchParams.get('gameType') || 'All Types');
  const [sortBy, setSortBy] = useState('members');

  // Load ALL clubs on mount
  useEffect(() => {
    const loadClubs = async () => {
      setLoading(true);
      setError(null);
      try {
        const apiUrl = getApiUrl();
        const res = await fetch(`${apiUrl}/clubs`);
        if (!res.ok) throw new Error('Failed to load clubs');
        const data = await res.json();
        if (data.success) {
          setClubs(data.data.map(club => ({
            id: club.id,
            slug: club.slug,
            name: club.name,
            location: club.location || '',
            members: club.memberCount || 0,
            memberCount: club.memberCount || 0,
            games: club.games || '',
            image: club.image,
            meetingDay: club.meetingDay || 'Contact for details',
            description: club.description || '',
            createdAt: club.createdAt,
          })));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadClubs();
  }, []);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('location', searchTerm);
    if (selectedGameType && selectedGameType !== 'All Types') params.set('gameType', selectedGameType);
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedGameType, setSearchParams]);

  // Filter + sort
  const filtered = clubs
    .filter(club => {
      const nameOK = !searchTerm ||
        club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (club.location || '').toLowerCase().includes(searchTerm.toLowerCase());
      const typeOK = selectedGameType === 'All Types' ||
        (club.games || '').toLowerCase().includes(selectedGameType.toLowerCase());
      const cityOK = !city ||
        (club.location || '').toLowerCase().includes(city.toLowerCase());
      return nameOK && typeOK && cityOK;
    })
    .sort((a, b) => {
      if (sortBy === 'members') return (b.memberCount || 0) - (a.memberCount || 0);
      if (sortBy === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === 'az') return a.name.localeCompare(b.name);
      return 0;
    });

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      {/* Page header */}
      <div className="bg-white border-b border-gray-100 py-10 px-4">
        <div className="container mx-auto max-w-6xl">
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-1">
            Find Board Game Clubs {city && <span className="text-primary">in {city}</span>}
          </h1>
          <p className="text-gray-500">Discover local communities, join sessions, and make friends through tabletop gaming.</p>
        </div>
      </div>

      {/* Sticky filter bar */}
      <div className="bg-white border-b border-gray-100 sticky top-16 z-30 shadow-sm">
        <div className="container mx-auto max-w-6xl px-4 py-3 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by name or location…"
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Game type filter */}
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-shrink-0">
            {GAME_TYPE_OPTIONS.map(opt => (
              <button
                key={opt}
                onClick={() => setSelectedGameType(opt)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  selectedGameType === opt ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-600 hover:border-primary hover:text-primary'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="flex-shrink-0 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white cursor-pointer"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Results */}
      <div className="flex-grow container mx-auto max-w-6xl px-4 py-8">
        {/* Results count */}
        {!loading && (
          <p className="text-sm text-gray-500 mb-6">
            Showing <span className="font-semibold text-gray-900">{filtered.length}</span> club{filtered.length !== 1 ? 's' : ''}
            {city ? ` in ${city}` : ''}
            {searchTerm ? ` matching "${searchTerm}"` : ''}
          </p>
        )}

        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow animate-pulse h-64" />
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-16">
            <p className="text-gray-500 mb-4">Failed to load clubs: {error}</p>
            <button onClick={() => window.location.reload()} className="px-5 py-2 bg-primary text-white rounded-full text-sm font-semibold">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
            <svg className="mx-auto w-16 h-16 text-gray-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-gray-600 font-semibold text-lg">No clubs found</p>
            <p className="text-gray-400 text-sm mt-1">
              {city ? `No clubs in ${city} yet — ` : 'No clubs match your filters — '}
              be the first to start one!
            </p>
            <div className="flex gap-3 justify-center mt-5">
              <button
                onClick={() => { setSearchTerm(''); setSelectedGameType('All Types'); }}
                className="px-5 py-2 border border-gray-200 text-gray-700 rounded-full text-sm font-semibold hover:border-primary hover:text-primary"
              >
                Clear Filters
              </button>
              <button
                onClick={() => navigate('/partner-with-us')}
                className="px-5 py-2 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary/90"
              >
                Start a Club
              </button>
            </div>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.07 } } }}
          >
            {filtered.map(club => (
              <motion.div
                key={club.id}
                variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } }}
              >
                <ClubCard
                  club={club}
                  onClick={() => navigate(`/clubs/${club.slug || club.id}`)}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default FindClubs;
